export class ElementSizeReadoutError extends Error {
  constructor(message, data = {}) {
    super(message);
    this.name = "ElementSizeReadoutError";
    this.data = data;
  }
}

export function layoutSizeOf(node) {
  if (!node) {
    throw new ElementSizeReadoutError("layoutSizeOf: no canvas node was given to measure");
  }
  if (!node.isConnected) {
    const identifier = node.getAttribute?.("data-edit-identifier") ?? "unknown";
    throw new ElementSizeReadoutError(
      `layoutSizeOf: the canvas node for ${identifier} is not attached to the document, so it has no layout size`,
      { identifier },
    );
  }
  return { width: node.offsetWidth, height: node.offsetHeight };
}

export function formatSizeReadout(px) {
  return `${Math.round(px)}px`;
}

export function editableNodeFor(identifier, root = document) {
  const node = root.querySelector(`[data-edit-identifier="${identifier}"]`);
  if (node) {
    return node;
  }
  const frames = Array.from(root.querySelectorAll("[data-breakpoint-id]"))
      .map(frame => frame.getAttribute("data-breakpoint-id"));
  throw new ElementSizeReadoutError(
    `editableNodeFor: no canvas node carries data-edit-identifier="${identifier}" `
      + `(canvas frames present: ${frames.length ? frames.join(", ") : "none"})`,
    { identifier, frames },
  );
}

export class ElementSizeTracker {
  #observer;
  #onMeasure;
  #onForget;
  #nodeByIdentifier = new Map();
  #identifierByNode = new Map();

  constructor(ResizeObserverImpl, { onMeasure, onForget }) {
    this.#onMeasure = onMeasure;
    this.#onForget = onForget;
    this.#observer = new ResizeObserverImpl(entries => this.#measure(entries));
  }

  observe(identifier, node) {
    const current = this.#nodeByIdentifier.get(identifier);
    if (current === node) {
      return;
    }
    if (current) {
      this.#observer.unobserve(current);
      this.#identifierByNode.delete(current);
    }
    this.#nodeByIdentifier.set(identifier, node);
    this.#identifierByNode.set(node, identifier);
    this.#observer.observe(node);
  }

  forget(identifier) {
    const node = this.#nodeByIdentifier.get(identifier);
    if (node) {
      this.#observer.unobserve(node);
      this.#identifierByNode.delete(node);
      this.#nodeByIdentifier.delete(identifier);
    }
    this.#onForget(identifier);
  }

  #measure(entries) {
    for (const { target } of entries) {
      const identifier = this.#identifierByNode.get(target);
      if (identifier === undefined || !target.isConnected) {
        continue;
      }
      this.#onMeasure(identifier, layoutSizeOf(target));
    }
  }
}
